// Pure helpers that distill the raw Strapi `esims` record (plus its
// embedded `variant_metadata`) into the shape the customer-facing UI
// actually wants to render. Centralised here so the list card and the
// detail page can render the same data the same way.

type AnyRecord = Record<string, unknown>

export type EsimSpecs = {
  // Quota / validity
  quotaMB: number | null
  totalDataBytes: number | null
  remainDataBytes: number | null
  validityDays: number | null
  isUnlimited: boolean
  isDaily: boolean

  // Coverage
  supportCountries: string[]
  network: NetworkInfo[]

  // Backend / connection
  apn: string | null
  provider: string | null

  // Feature toggles
  canHotspot: boolean
  canPhoneNumber: boolean
  canTopup: boolean
  autoTopupEnabled: boolean

  // Copy from variant
  description: string
  notice: string

  // Lifecycle
  installEndTime: string | null
  isFreeEsim: boolean
}

export type NetworkInfo = {
  country: string
  operators: string[]
  generations: string[]
}

function readString(record: AnyRecord, key: string): string | null {
  const value = record[key]
  if (typeof value === "string" && value.trim().length > 0) return value.trim()
  if (typeof value === "number") return String(value)
  return null
}

function readBool(record: AnyRecord, key: string): boolean {
  const value = record[key]
  if (value === true) return true
  if (value === "true" || value === "1" || value === 1) return true
  return false
}

function readNumber(record: AnyRecord, key: string): number | null {
  const value = record[key]
  if (value === null || value === undefined || value === "") return null
  const numeric = Number(value)
  return Number.isFinite(numeric) ? numeric : null
}

function readStringArray(record: AnyRecord, key: string): string[] {
  const value = record[key]
  if (Array.isArray(value)) {
    return value
      .map((entry) => (typeof entry === "string" ? entry.trim() : ""))
      .filter((entry): entry is string => entry.length > 0)
  }
  // Some legacy records ship the array as a JSON string (e.g.
  // network was "[\"TW--Chunghwa--3G\"]" before Strapi started returning
  // proper arrays). Best-effort parse.
  if (typeof value === "string" && value.trim().startsWith("[")) {
    try {
      const parsed = JSON.parse(value)
      if (Array.isArray(parsed)) {
        return parsed
          .map((entry) => (typeof entry === "string" ? entry.trim() : ""))
          .filter((entry): entry is string => entry.length > 0)
      }
    } catch {
      // fallthrough — return empty
    }
  }
  return []
}

function readTranslationsMap(
  variantMeta: AnyRecord,
  baseKey: string
): Record<string, string> | null {
  // New canonical shape (populated by the LLM batch translator + plan-import
  // hook in yohomobile-v2): variant_metadata.description_translations or
  // notice_translations is an object keyed by locale. Marker entries we add
  // for bookkeeping (e.g. `_source_hash`) start with `_` and are ignored.
  const raw = variantMeta[`${baseKey}_translations`]
  if (!raw || typeof raw !== "object") return null
  const map: Record<string, string> = {}
  for (const [key, value] of Object.entries(raw as Record<string, unknown>)) {
    if (key.startsWith("_")) continue
    if (typeof value === "string" && value.trim().length > 0) {
      map[key] = value.trim()
      continue
    }
    // The batch translator wraps low-confidence entries as
    // { text, needs_review: true } so they can be flagged for human review.
    // Render them anyway — better localised text than English fallback.
    if (value && typeof value === "object") {
      const nested = (value as AnyRecord).text
      if (typeof nested === "string" && nested.trim().length > 0) {
        map[key] = nested.trim()
      }
    }
  }
  return Object.keys(map).length > 0 ? map : null
}

/**
 * Pick the localized text for `<baseKey>` (e.g. "description" / "notice")
 * from a Strapi-style variant_metadata object. Reads the new canonical
 * `<baseKey>_translations` map first, falls back to legacy
 * `<baseKey>_<lang>` fields. Exported so callers outside the eSIM detail
 * page (checkout, thankyou) can share the same fallback chain.
 */
export function pickLocalizedCopy(
  variantMeta: AnyRecord,
  baseKey: string,
  lng: string
): string {
  // Preferred path: the LLM-populated `<baseKey>_translations` map. Falls
  // back to the legacy `<baseKey>_en` / `<baseKey>_cn` / `<baseKey>` fields
  // for variants that haven't been backfilled yet.
  const primary = lng.toLowerCase().split("-")[0] || "en"
  const translations = readTranslationsMap(variantMeta, baseKey)
  if (translations) {
    const candidates = [lng, lng.toLowerCase(), primary, "en"]
    for (const k of candidates) {
      const hit = translations[k]
      if (hit) return hit
    }
    // Loose match on language prefix (e.g. ?lng=zh → translations["zh-CN"]).
    for (const [k, v] of Object.entries(translations)) {
      if (k.toLowerCase().split("-")[0] === primary && v) return v
    }
  }

  const legacyCandidates = [
    `${baseKey}_${primary}`,
    `${baseKey}_${lng.toLowerCase()}`,
    `${baseKey}_en`,
    baseKey,
  ]
  for (const key of legacyCandidates) {
    const value = variantMeta[key]
    if (typeof value === "string" && value.trim().length > 0) {
      return value.trim()
    }
  }
  return ""
}

/**
 * Parse one entry from `variant_metadata.network`. Format observed in prod:
 *   "JP--KDDI__Softbank--4G__3G__4G__5G"
 *   country ISO -- operators (underscore-separated) -- generations
 *
 * Empty / malformed entries get a country-only record so we don't drop
 * coverage info.
 */
function parseNetworkDescriptor(raw: string): NetworkInfo {
  const segments = raw.split("--")
  const country = (segments[0] || "").trim().toUpperCase()
  const operators = (segments[1] || "")
    .split("__")
    .map((entry) => entry.trim())
    .filter((entry) => entry.length > 0)
  // Generations sometimes carry duplicates (e.g. 4G appears twice for the
  // joint KDDI+Softbank case). Dedupe while preserving order.
  const generationsRaw = (segments[2] || "")
    .split("__")
    .map((entry) => entry.trim().toUpperCase())
    .filter((entry) => entry.length > 0)
  const generations = Array.from(new Set(generationsRaw))
  return { country, operators, generations }
}

export function extractEsimSpecs(esim: AnyRecord): EsimSpecs {
  const variantMeta =
    (esim.variant_metadata as AnyRecord | null | undefined) || {}

  const quotaMB =
    readNumber(esim, "quota") ?? readNumber(variantMeta, "quota") ?? null
  const validityDays =
    readNumber(esim, "day") ?? readNumber(variantMeta, "day") ?? null

  const networkRaw = readStringArray(variantMeta, "network")
  const network = networkRaw.map(parseNetworkDescriptor)

  // Coverage prefers explicit support_countries; falls back to the country
  // codes derived from network descriptors so we never render "no coverage".
  const supportCountriesArray = readStringArray(variantMeta, "support_countries")
  const supportCountries =
    supportCountriesArray.length > 0
      ? supportCountriesArray.map((entry) => entry.toUpperCase())
      : Array.from(
          new Set(network.map((entry) => entry.country).filter(Boolean))
        )

  return {
    quotaMB,
    totalDataBytes: readNumber(esim, "total_data_bytes"),
    remainDataBytes: readNumber(esim, "remain_data_bytes"),
    validityDays,
    isUnlimited: readBool(variantMeta, "isUnlimited"),
    isDaily: readBool(variantMeta, "daily"),
    supportCountries,
    network,
    apn: readString(variantMeta, "apn"),
    provider: readString(variantMeta, "provider"),
    canHotspot:
      readBool(esim, "can_hotspot") || readBool(variantMeta, "hotspot"),
    canPhoneNumber:
      readBool(esim, "can_phonenumber") || readBool(variantMeta, "phone_number"),
    canTopup: readBool(esim, "can_topup"),
    autoTopupEnabled: readBool(esim, "auto_topup_enable"),
    description: readDescription(variantMeta, "en"),
    notice: readNotice(variantMeta, "en"),
    installEndTime: readString(esim, "install_end_time"),
    isFreeEsim: readBool(esim, "is_free_esim"),
  }
}

function readDescription(variantMeta: AnyRecord, lng: string): string {
  return pickLocalizedCopy(variantMeta, "description", lng)
}

function readNotice(variantMeta: AnyRecord, lng: string): string {
  return pickLocalizedCopy(variantMeta, "notice", lng)
}

/**
 * Pick the right localized description / notice for the active UI locale.
 * Kept separate from extractEsimSpecs because the spec object is built once
 * (server-shape) and the UI may switch locale without refetch.
 */
export function getLocalizedDescription(
  esim: AnyRecord,
  lng: string
): string {
  const variantMeta = (esim.variant_metadata as AnyRecord | null | undefined) || {}
  return readDescription(variantMeta, lng)
}

export function getLocalizedNotice(esim: AnyRecord, lng: string): string {
  const variantMeta = (esim.variant_metadata as AnyRecord | null | undefined) || {}
  return readNotice(variantMeta, lng)
}

/** Human-readable quota label: 1024 MB → "1 GB", 500 MB → "500 MB". */
export function formatQuotaLabel(quotaMB: number | null): string {
  if (quotaMB === null || quotaMB <= 0) return ""
  if (quotaMB >= 1024 && quotaMB % 1024 === 0) {
    return `${quotaMB / 1024} GB`
  }
  if (quotaMB >= 1024) {
    return `${(quotaMB / 1024).toFixed(1)} GB`
  }
  return `${quotaMB} MB`
}

/** "1 GB used / 1 GB" style label from raw byte counts. */
export function formatDataUsageBytes(
  usedBytes: number | null,
  totalBytes: number | null
): { used: string; total: string; pct: number } | null {
  if (
    usedBytes === null ||
    totalBytes === null ||
    !Number.isFinite(usedBytes) ||
    !Number.isFinite(totalBytes) ||
    totalBytes <= 0
  ) {
    return null
  }
  return {
    used: formatBytes(usedBytes),
    total: formatBytes(totalBytes),
    pct: Math.min(100, Math.max(0, Math.round((usedBytes / totalBytes) * 100))),
  }
}

function formatBytes(bytes: number): string {
  if (bytes <= 0) return "0 MB"
  const mb = bytes / (1024 * 1024)
  if (mb >= 1024) {
    return `${(mb / 1024).toFixed(mb % 1024 === 0 ? 0 : 1)} GB`
  }
  if (mb >= 1) {
    return `${Math.round(mb)} MB`
  }
  return `${Math.round(bytes / 1024)} KB`
}

/**
 * Format a list of ISO country codes for display, using the customer's
 * locale via Intl.DisplayNames. Long lists collapse to "Japan + 5 more".
 */
export function formatCoverageLabel(
  countryCodes: string[],
  lng: string,
  maxBeforeTruncate: number = 3
): string {
  if (countryCodes.length === 0) return ""
  const display = new Intl.DisplayNames([lng], {
    type: "region",
    fallback: "code",
  })
  const names = countryCodes.map((code) => {
    try {
      return display.of(code.toUpperCase()) || code
    } catch {
      return code
    }
  })
  if (names.length <= maxBeforeTruncate) return names.join(" · ")
  const head = names.slice(0, maxBeforeTruncate).join(" · ")
  return `${head} +${names.length - maxBeforeTruncate}`
}

/**
 * Collapse the network array into a deduplicated set of {operators,
 * generations} suitable for one-line display, e.g.
 *   "KDDI · Softbank — 3G/4G/5G"
 *
 * When the entries span multiple distinct operator sets, we keep them as a
 * list (rare) — the detail page renders each entry on its own row.
 */
export function summarizeNetwork(network: NetworkInfo[]): {
  operators: string[]
  generations: string[]
} {
  const operators = new Set<string>()
  const generations = new Set<string>()
  network.forEach((entry) => {
    entry.operators.forEach((op) => operators.add(op))
    entry.generations.forEach((gen) => generations.add(gen))
  })
  return {
    operators: Array.from(operators),
    generations: Array.from(generations),
  }
}

/**
 * Provider code → human name. Mapping mirrors yoho-v2's getProviderType
 * (see public/main.dart.js + yoho-v2/refresh_esim/route.ts):
 *   C = CMLink, T = Telna, J = Joytel, N = Noname, M = MB.
 */
export function formatProviderName(provider: string | null): string {
  if (!provider) return ""
  switch (provider.toUpperCase()) {
    case "C":
      return "CMLink"
    case "T":
      return "Telna"
    case "J":
      return "Joytel"
    case "N":
      return "Noname"
    case "M":
      return "MB"
    default:
      return provider
  }
}
